import './mask-field.scss'

import { faEraser, faPlusCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import _ from 'lodash';
import { first, last } from 'rxjs/operators';
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator';

import { TranslatedString, TranslationDataStore } from '@skinsoft/i18n';
import { ReactiveVue } from '@skinsoft/reactive-vue';

import { buildSkinDate, createTranslatedString, EditableTranslatedStringList, EntitySelect, getTranslatedString, skinDateToInputDate, toDateTxt } from '@skinsoft/ui';

export interface EntityField {
    id: string,
    isReadOnly: boolean,
    entity: string,
    abstractEntity: string,
    schema: string,
    name: string,
    type: any,
    path: string,
    inputComponent: {
        name: string
    },
    entityFields: any[]
}

const lastId = 1;

@Component({
    components: {
        sEditableTranslatedStringList: EditableTranslatedStringList,
        sEntitySelect: EntitySelect,
    },
    props: ['entityField', 'fieldValue', 'forceReadonly', 'isInMetaMask'],
    template: require('./mask-field.html')
})
export class MaskField extends ReactiveVue {

    public entityField: EntityField;
    public fieldValue: any;
    public forceReadonly: boolean;
    public isInMetaMask: boolean;

    private entity: any;

    private fieldLabel: string = "";

    private value: any = null;

    private startValue: any = ""

    // Icons
    private eraseIcon = faEraser
    private plusIcon = faPlusCircle;
    private trashIcon = faTrash;

    private singleComplex = ['RealTimeLocation', 'CurrentItemState', 'PermanentLocation']

    private $translationDataStore: TranslationDataStore;
    private get valueAsDateReadonly() {
        if (this.entityField.type.type === "SkinDate") {
            const txtValue = (this.fieldValue?.txt) as string
            toDateTxt(this.fieldValue?.startInterval?.txt);
        }
        return "";
    }

    private fetchLabel(key: string) {
        let translationKey: string = `field.${key}`
        if (this.entityField.entityFields
            && this.entityField.entityFields.length > 0) {
            translationKey += ".complex"
        }
        this.subscribeTo(
            this.$translationDataStore.getMessage(translationKey, this.$i18n.locale).pipe(first()),
            translation => {
                this.fieldLabel = translation
                this.setStartValue()
            },
            'translation-subscription'
        )
    }

    /**
     * Sets the field value according to its corresponding entityField value
     * Adapts the shape if necesseray, according to entityField type
     */
    private setStartValue() {
        switch (this.entityField.type.type) {
            case "TranslatedString":
                if (this.fieldValue) {
                    this.value = getTranslatedString(this.fieldValue, this.$i18n.locale);
                } else {
                    this.value = "";
                }
                break;
            case "SkinDate":
                if (this.fieldValue) {
                    const txtValue = (this.fieldValue.txt) as string
                    this.value = !!txtValue ? txtValue.split('/').reverse().join('-') : skinDateToInputDate(this.fieldValue);
                } else {
                    this.value = null;
                }
                break;
            case "List":
                if (this.fieldValue) {
                    this.value = this.fieldValue
                } else {
                    this.value = [];
                    this.addEntry()
                }
                break;
            default:
                if (this.fieldValue) {
                    this.value = this.fieldValue
                }
                break
        }
    }

    private thesaurusIdentifierIfNeeded(entityField: EntityField) {
        if (entityField.type.type === "ThesaurusTerm") {
            return entityField.type.thesaurusIdentifier;
        }

        if (entityField.type.entityFieldType?.type === "ThesaurusTerm") {
            return entityField.type.entityFieldType.thesaurusIdentifier
        }

        return null;
    }

    private eraseValue() {
        this.value = null;
    }

    private onEntitySelected(entity: any) {
        this.value = entity.id;
    }

    private onEntitySelectedInList(entity: any, index) {
        if (!this.value) {
            this.value = {}
        }
        this.value[index] = entity.id;
    }

    private updateValue(event: any, index: number, entityFieldName: string) {
        if (!this.value) {
            this.value = {}
        }
        this.value[index][entityFieldName] = event.value;
    }

    private updateNonListValue(event: any, entityFieldName: string) {
        if (!this.value) {
            this.value = {}
        }
        this.value[entityFieldName] = event.value
    }

    private needToSpan(entityField: EntityField) {
        return (entityField.type.type === "List" || this.singleComplex.includes(entityField.type.type)) && !entityField.isReadOnly;
    }

    // Methods when value is a list
    private addEntry() {
        this.value.push({})
    }

    private removeEntry(index: number) {
        this.value.splice(index, 1)
    }

    private isReadonly(entityField: EntityField) {
        return entityField.isReadOnly || this.forceReadonly;
    }

    private getTranslatedString(translatedString: TranslatedString) {
        return getTranslatedString(translatedString, this.$i18n.locale);
    }

    /**
     * Reshapes value if necessary before sending it to backend
     */
    private adaptValueAndEmit() {
        let returnValue = this.value;
        switch (this.entityField.type.type) {
            case "TranslatedString":
                if (this.value) {
                    returnValue = createTranslatedString(this.$i18n.locale, this.value);
                } else {
                    this.value = null;
                }
                break;
            case "SkinDate":
                if (this.value) {
                    const reshapedDateTxt = this.value.split('-').reverse().join('/')
                    returnValue = buildSkinDate(reshapedDateTxt);
                } else {
                    returnValue = null
                }
                break;
            default:
                returnValue = this.value;
                break
        }
        this.$emit("value-change", {
            abstractEntity: this.entityField.abstractEntity,
            fieldPath: this.entityField.path,
            value: returnValue,
        })
    }

    @Watch('value', { deep: true })
    private onValueChange() {
        this.adaptValueAndEmit()
    }

    @Watch('entityField', { immediate: true, deep: false })
    private onEntityFieldChange() {
        this.unsubscribeFrom('translation-subscription')
        this.fetchLabel(this.entityField.path);
    }

    @Watch('fieldValue')
    private onFieldLabelChange() {
        this.setStartValue()
    }

}